
require("./../../node_modules/@module-federation/nextjs-mf/src/include-defaults");
import "react-singleton-context";
import { useEffect } from "react";
import App, { AppContext, AppProps, AppInitialProps } from "next/app";
import Head from "next/head";
import { UserProfile } from "@auth0/nextjs-auth0/client";

import AppProviders from "@/components/AppProviders";
import Layout from "@/components/Layout";
import useDataDog from "@/hooks/useDataDog";
import type { LD } from "../server-utils/getFlagsServer";
import type { DivvyExp } from "../server-utils/getExperimentsServer";

import "@carvana/showroom-css-theme/Reset.css";
import "@carvana/showroom-css-theme/Theme.css";
import "@carvana/showroom-css-theme/Font.css";
import "@carvana/helios-styles/reference.css";
import "@carvana/helios-styles/adesa-theme.css";
import "react-loading-skeleton/dist/skeleton.css";
import "../styles/globalStyles.css";

if (process.env.NEXT_PUBLIC_API_MOCKING === "enabled") {
  require("../mocks");
}

let getExperimentsServerModule:
  | undefined
  | Promise<typeof import("../server-utils/getExperimentsServer")>;
let getFlagsServerModule:
  | undefined
  | Promise<typeof import("../server-utils/getFlagsServer")>;
if (typeof window === "undefined") {
  getFlagsServerModule = import("../server-utils/getFlagsServer");
  getExperimentsServerModule = import("../server-utils/getExperimentsServer");
}
if (process.env.NEXT_PUBLIC_DEPLOY_ENV !== "prod") {
  // eslint-disable-next-line no-console
  console.log(
    "__webpack_share_scopes__",
    typeof window !== "undefined" ? "browser" : "server",
  );
  // eslint-disable-next-line no-console, no-undef
  // @ts-expect-error: module federation global
  console.dir(__webpack_share_scopes__, { depth: null });
}

// Extra props returned from MyApp.getInitialProps
type AppOwnProps = { ld?: LD; experiments?: DivvyExp };

const MyApp = ({
  Component,
  pageProps,
  ld,
  experiments,
}: AppProps<{ user?: UserProfile }> & AppOwnProps) => {
  const { user } = pageProps;
  useDataDog(user);

  useEffect(() => {
    document.body.setAttribute(
      "data-theme",
      ld?.features["auction-theme"] || "Adesa",
    );
  }, [ld]);

  return (
    <>
      <Head>
        <title>ADESA Clear</title>
      </Head>
      <AppProviders auth0User={user} ld={ld} experiments={experiments}>
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </AppProviders>
    </>
  );
};

MyApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppOwnProps & AppInitialProps> => {
  const appProps = await App.getInitialProps(appContext);

  if (appContext.ctx.req && appContext.ctx.res) {
    let ld;
    if (getFlagsServerModule) {
      const getFlagsServer = (await getFlagsServerModule).default;
      ld = await getFlagsServer(appContext.ctx.req, appContext.ctx.res);
    }

    let experiments;
    if (getExperimentsServerModule) {
      const getExperimentsServer = (await getExperimentsServerModule).default;
      experiments = await getExperimentsServer(
        appContext.ctx.req,
        appContext.ctx.res,
        ld?.features["auction-enabled-experiments"],
      );
    }

    return { ...appProps, ld, experiments };
  }

  return appProps;
};

export default MyApp;
